import React, {createContext, useEffect, useState} from 'react';
import {useQuery} from "@tanstack/react-query";
import {v4 as uuidv4} from "uuid";
import UserInitialData from "../utils/user-initial-data.jsx";
import General from "../components/tabs/general.jsx";
import User from "../components/tabs/user.jsx";
import Documents from "../components/tabs/documents.jsx";
import Trainings from "../components/tabs/trainings.jsx";
import ConductHistory from "../components/tabs/conduct-history.jsx";
import PrivateWeapons from "../components/tabs/private-weapons.jsx";
import DayOff from "../components/tabs/day-off.jsx";
import Clothes from "../components/tabs/clothes.jsx";
import UserDevices from "../components/tabs/devices/devices.jsx";
import Dependent from "../components/tabs/dependent.jsx";
import SkillsRestriction from "../components/tabs/skills-restriction.jsx";
import Resume from "../components/tabs/resume.jsx";
import projectUtils from "../../../../../utils/project-utils.js";
import userService from "../../../../../services/web/user-service.js";
import {useParams} from "react-router";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";
import Attachs from "../components/tabs/attachs.jsx";

export const AgentContext = createContext(undefined);

const sharedInitial = {
    sharedDevice: null,
    shareEmail: false,
    shareContactInfo: false,
    focalPoints: []
}

const AgentProvider = ({children}) => {
    const {id} = useParams()
    const [user, setUser] = useState(UserInitialData);
    const [shared, setShared] = useState(sharedInitial);
    const [devices, setDevices] = useState([]);
    const [rolesSelected, setRolesSelected] = useState([]);
    const [isLoading, setIsLoading] = useState(false)
    const [groupsSelected, setGroupsSelected] = useState([]);
    const [attachs, setAttachs] = useState([]);
    const [tablesAttachs, setTablesAttachs] = useState([]);
    const [generalAttachs, setGeneralAttachs] = useState([]);
    const [attachesDescription, setAttachesDescription] = useState({});
    const [changes, setChangesContext] = useState([])
    const [allowedTabs, setTabs] = useState([])
    const [lanes, setLanes] = useState([])
    const [openCpfModal, setOpenCpfModal] = useState(false)
    const [readyToBlock, setReadyToBlock] = useState(false)
    const [tabSelected, setTabSelected] = useState("general");
    const [cpfFocus, setCpfFocus] = useState(false);
    const [passWordFocus, setPassWordFocus] = useState(false);
    const [passwordComplexity, setPasswordComplexity] = useState(false)
    const [existsCpf, setExistsCpf] = useState(false)

    const [printResume, setPrintResume] = useState(null);
    const [headerSelected, setHeaderSelected] = useState(-1);

    const printFields = [
        { key: "address", label: "Endereço" },
        { key: "phones", label: "Telefones" },
        { key: "documents", label: "Documentos", permission: PermissionEnum.USER_DOCUMENTS },
        { key: "trainings", label: "Formações e aperfeiçoamento", permission: PermissionEnum.USER_TRAININGS },
        { key: "conductHistory", label: "Histórico de conduta", permission: PermissionEnum.USER_CONDUCT_HISTORY },
        { key: "privateWeapons", label: "Armas particulares", permission: PermissionEnum.USER_PRIVATE_WEAPONS },
        { key: "dayOff", label: "Ausências", permission: PermissionEnum.USER_ABSENCES },
        { key: "clothes", label: "Vestuário", permission: PermissionEnum.USER_CLOTHES },
        { key: "dependents", label: "Dependentes", permission: PermissionEnum.USER_DEPENDENTS },
        { key: "skillsRestrictions", label: "Aptidões e restrições", permission: PermissionEnum.SKILLS_RESTRICTIONS },
    ];

    const initialPrintOptions = Object.fromEntries(printFields.map((item) => [item.key, true]));

    const [printModal, setPrintModal] = useState(false);
    const [printOptions, setPrintOptions] = useState(initialPrintOptions);

    const tabs = [
        { key: "resume", component: <Resume/>, label: 'Resumo', permission: ""},
        { key: "general", component: <General/>, label: 'Informações pessoais', permission: ""},
        { key: "user", component: <User/>, label: "Acessos", permission: PermissionEnum.USER},
        { key: "devices", component: <UserDevices/>, label: "Telefones e APPs", permission: ""},
        { key: "documents", component: <Documents/>, label: "Documentos", permission: PermissionEnum.USER_DOCUMENTS},
        { key: "training", component: <Trainings/>, label: "Formação e aperfeiçoamento", permission: PermissionEnum.USER_TRAININGS},
        { key: "conduct_history", component: <ConductHistory/>, label: "Histórico de conduta", permission: PermissionEnum.USER_CONDUCT_HISTORY},
        { key: "private_weapons", component: <PrivateWeapons/>, label: "Armas particulares", permission: PermissionEnum.USER_PRIVATE_WEAPONS},
        { key: "dayOff", component: <DayOff/>, label: "Ausências", permission: PermissionEnum.USER_ABSENCES},
        { key: "clothes", component: <Clothes/>, label: "Vestuário", permission: PermissionEnum.USER_CLOTHES},
        { key: "dependents", component: <Dependent/>, label: "Dependentes", permission: PermissionEnum.USER_DEPENDENTS},
        { key: "skills_restrictions", component: <SkillsRestriction/>, label: "Aptidões e restrições", permission: PermissionEnum.SKILLS_RESTRICTIONS},
        { key: "attachs", component: <Attachs/>, label: "Anexos", permission: PermissionEnum.USER_ATTACHS },
    ];

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const userParam = searchParams.get("user");
        if (userParam) {
            const newUser = JSON.parse(atob(userParam));

            const {email, birthDate, fatherName, motherName, login, name, scholarity, department, role, matriculation} = newUser;
            setUser(prev => (
                {
                    ...prev,
                    general: {
                        ...user.general,
                        rank: role,
                        employeeCode: matriculation,
                        email,name,
                        cpf: login,
                        profile: {
                            ...user.general.profile,
                            dtBirth: birthDate,
                            dtBirthValue: formatDateManually(birthDate),
                            father: fatherName,
                            mother: motherName,
                            sector: department,
                            educationLevel: getEducationLeve(scholarity)
                        }
                    },
                    user: {
                        ...user.user,
                        login: login.replaceAll(".", "").replaceAll("-", "")
                    }
                }
            ))
        } else if (!id) {
            setOpenCpfModal(true)
        }
    }, []);

    const getEducationLeve = (value) => {
        if (!value) return value;
        switch (value) {
            case "Não alfabetizado": return "illiterate"
            case "Fundamental": return "elementarySchool"
            case "Médio": return "highSchool"
            case "Superior": return "university"
            case "Pós-graduação": return "postGraduate"
            case "Mestrado": return "master"
            case "Doutorado": return "doctorate"
        }
    }

    const handleInputObject = (object, {target: {value, id}}) => {
        handleChanges(object)
        setUser(prev => ({...prev, [object]: {...prev[object], [id]: value}}))
    }

    const handleDocsInputObject = (object, value, id) => {
        handleChanges(object)
        setUser(prev => ({...prev, [object]: {...prev[object], [id]: value}}))
    }

    const setChanges = (target) => setChangesContext(prev => (prev.includes(target) ? prev : [...prev, target]));

    const handleChanges = (object) => {
        const target = getTabName(object);
        setChangesContext(prev => (prev.includes(target) ? prev : [...prev, target]));
    }

    const getTabName = (target) => {
        if (target === "profile") return "general"
        if (target === "conductHistory") return "conduct_history"

        return target;
    }

    const {data: userInfo} = useQuery({
        queryFn: () =>  userService.getInfo().then((data) => data),
        queryKey: ['user-get-info']
    });

    const {data: userFetch, refetch: userRefetch} = useQuery({
        queryFn: () => {
            setIsLoading(true);

            return userService.get(id)
                .then((data) => {
                    if (data.dayOff) {
                        data.dayOff = data.dayOff.map(d => {
                            return {...d, itemId: uuidv4()}
                        })
                    }

                    data.devices = data.devicesView;
                    delete data.devicesView

                    const attachsDescription = (data?.attachs ?? []).reduce((acc, attach) => {
                        acc[attach.attachId] = attach.description ?? null;
                        return acc;
                    }, {});

                    setAttachesDescription(attachsDescription);

                    replaceNulls(data)
                    setUser(data);
                    return data;
                }).finally(() => setIsLoading(false));
        },
        queryKey: ['user'],
        enabled: !!id
    });

    useEffect(() => {
        if (userFetch && userInfo) {
            const roles = userFetch.user?.roles ?? [];
            const groups = userFetch.user?.groups ?? [];

            if (userInfo.roles) {
                setRolesSelected(userInfo.roles.filter(r => roles.includes(parseInt(r.value))));
            }

            if (userInfo.groups) {
                setGroupsSelected(userInfo.groups.filter(g => groups.includes(parseInt(g.value))));
            }

            setDevices(userFetch.devices.map(d =>
                ({
                    ...d,
                    pcl: userInfo.mobileLanes.find(l => parseInt(l.value) === d.pclLaneCode),
                    agentLane: userInfo.mobileLanes.find(l => parseInt(l.value) === d.agentLaneCode),
                    value: d.id,
                    label: projectUtils.formatPhone(d.phone)
                })
            ));

            setLanes(userInfo.mobileLanes);

            const sharedDevice = userFetch.devices.find(d => d.id === userFetch.general.shareDeviceId)

            setShared({
                shareEmail: userFetch.general.shareEmail,
                shareContactInfo: userFetch.general.shareContactInfo,
                sharedDevice: sharedDevice ? {value: sharedDevice.id, label: projectUtils.formatPhone(sharedDevice.phone)} : null,
                focalPoints: userInfo.focalPoints.filter(fp => userFetch.general.focalPoints.includes(fp.value)),
            });

            setPasswordComplexity(userInfo.hasPasswordComplexity)
        }

        if (userInfo) {
            setTabs(tabs.filter(tab =>
                userInfo.userLoggedPermissions.includes(tab.permission) || tab.permission === ""
            ));
        }

    }, [userFetch, userInfo]);

    const replaceNulls = (obj) => {
        for (const key in obj) {
            if (obj[key] === null) {
                obj[key] = UserInitialData[key] ?? "";
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                replaceNulls(obj[key]);
            }
        }
    }

    return (
        <AgentContext.Provider value={{
            user,
            setUser,
            shared,
            setShared,
            devices,
            setDevices,
            userInfo,
            groupsSelected,
            setGroupsSelected,
            rolesSelected,
            setRolesSelected,
            id,
            passwordComplexity,
            printFields,
            printModal, setPrintModal,
            printOptions, setPrintOptions,
            printResume, setPrintResume,
            headerSelected, setHeaderSelected,
            attachs, setAttachs,
            generalAttachs, setGeneralAttachs,
            attachesDescription, setAttachesDescription,
            handleInputObject,
            handleDocsInputObject,
            tabSelected, setTabSelected,
            tablesAttachs, setTablesAttachs,
            cpfFocus, setCpfFocus,
            passWordFocus, setPassWordFocus,
            isLoading, setIsLoading, changes, setChanges, userRefetch, setChangesContext, tabs: allowedTabs,
            lanes, openCpfModal, setOpenCpfModal, readyToBlock, setReadyToBlock, existsCpf, setExistsCpf
        }}>
            {children}
        </AgentContext.Provider>
    );
};

export default AgentProvider;
